import { Theme } from './theme';
import { ProductReview } from './comment';
import { Coupon } from './coupon';
import { Order } from './payment';
import { User } from './user';

export enum ProductBookingMode {
  INSTANT = 'INSTANT',
  APPROVAL = 'APPROVAL',
}

export class Product {
  /** 어드민 목록 전용 — 저장 후 자동 번역이 트리거됐으나 아직 완료되지 않음(번역중). 백엔드 어드민 목록이 채운다. */
  translating?: boolean;

  /** 어드민 목록 전용 — 번역된(PENDING 아님) 언어 목록(en/ja). 번역됨/부분 번역/미번역 배지 도출에 쓰인다. 백엔드 어드민 목록이 채운다. */
  translatedLanguages?: string[];

  /** 상품에 부여된 테마. 어드민 목록(searchByTermAdmin)이 채운다. */
  themes?: Theme[];

  /**
   * 예약 가능한 미래 회차 보유 여부. 백엔드가 채우는 비정규화 플래그로, 클라이언트는 읽기만 한다.
   * 홈 섹션(nnb-nextjs `fetchSectionsCached.ts`)은 이 값이 false인 상품을 어드민 지정 순서와
   * 무관하게 섹션 하단으로 민다.
   *
   * 두 가지 한계가 있으니 "예약 가능"과 동일시하지 말 것:
   * - 정원이 찬(매진) 회차는 판정에서 빠져 있어, 완전 매진 상품도 true로 남는다.
   * - 티켓 변경 훅 + 매시 cron으로 갱신돼 최대 1시간 stale할 수 있다.
   */
  isReservable?: boolean;

  /** 예약 확정 방식. 값이 없는 구버전 응답은 `INSTANT`로 취급한다. */
  bookingMode?: ProductBookingMode;

  /** 호스트 목록 전용 — 위저드 필수 입력이 비어 있어 「작성 중」으로 보여줄 상품. 백엔드 searchHosted 가 채운다. */
  isDraft?: boolean;

  /**
   * 엔티티·DB에서 이미 제거된 레거시 정렬값. `getRefinedProduct` 응답에만 실려 온다(다른 목록
   * API는 안 채움). 구버전 NestJS가 아직 떠 있는 배포 윈도우에 `PUT /api/products/:id`가 이
   * 필드를 필수로 요구해 그대로 돌려보내야 한다(`ProductService.updateProduct`).
   */
  sortOrder?: number | null;

  /** 할인가 계산에 쓸 지정 쿠폰. 비어 있으면 받을 수 있는 쿠폰 중 최대 할인으로 자동 계산한다(NNB-912). */
  discountCouponId?: number | null;

  constructor(
    public id: number,
    public title: string,
    public price: number,
    public discountPrice: number,
    public recommend: string,
    public version: number,
    public address: string,
    public detailAddress: string,
    public howToCome: string,
    public runningDays: number,
    public runningHours: number,
    public runningMinutes: number,
    public reservationHours: number,
    public lat: number,
    public lon: number,
    public host: User,
    public notice: string,
    public checkList: string,
    public includeList: string,
    public excludeList: string,
    public refundPolicy: string,
    public status: ProductStatus,
    public isBusiness: boolean,
    public isOffline: boolean,
    public color: string,
    public representationCategory: ProductRepresentationCategory,
    public description?: string,
    public point?: string,
    public summaries?: ProductSummary[],
    public sections?: ProductSection[],
    public closedTicketDate?: ClosedTicketDate[],
    public hashtags?: Hashtag[],
    public amenities?: Amenity[],
    public likes?: number,
    public isSetLike?: boolean,
    public tickets?: Ticket[],
    public createdAt?: Date,
    public updatedAt?: Date,
    public representationPhotos?: ProductRepresentationPhoto[] | ProductRepresentationPhotoCreateDto[],
    public courses?: Course[] | CourseCreateDto[],
    public productReviews?: ProductReview[],
    public productConfirms?: ProductConfirm[],
    public onlineMethod?: OnlineMehtod,
    public orders?: Order[],
    public reviewFeedCount?: number,
    public badge = '',
    public badgeLabel = '',
    public hostCommissionRate?: number,
    public isPb?: boolean,
    public isCommerce?: boolean,
    public coupons?: Coupon[],
    public country?: string,
    public sido?: string,
    public sigugun?: string,
    public dongmyun?: string,
    public ri?: string,
    public rest?: string,
    public priceImage?: string,
    public bizName?: string,
    public agency?: string,
    public agencyImage?: string,
    public minParticipants?: number,
    public maxParticipants?: number,
    public nonMemberPurchasable?: boolean,
    public specialDealLabelOnTop?: boolean,
    public businessGroupId?: number,
    public tripType?: TripType,
  ) {}
}

export class ClosedTicketDate {
  constructor() {}
  public date: Date;
}

export class ProductCreateDto {
  constructor(
    public version: number,
    public title: string,
    public recommend: string,
    public address: string,
    public detailAddress: string,
    public howToCome: string,
    public runningDays: number,
    public runningHours: number,
    public runningMinutes: number,
    public reservationHours: number,
    public lat: number,
    public lon: number,
    public country: string,
    public sido: string,
    public sigugun: string,
    public dongmyun: string,
    public ri: string,
    public rest: string,
    public price: number,
    public discountPrice: number | null,
    public notice: string,
    public checkList: string,
    public includeList: string,
    public excludeList: string,
    public refundPolicy: string,
    public status: ProductStatus,
    public isBusiness: boolean,
    public isOffline: boolean,
    public hashtags: HashtagCreateDto[],
    public representationPhotos: ProductRepresentationPhotoCreateDto[],
    public color: string,
    public point?: string,
    public description?: string,
    public summaries?: ProductSummary[],
    public sections?: ProductSection[],
    public amenities?: Amenity[],
    public courses?: CourseCreateDto[],
    public onlineMethod?: OnlineMehtod,
    public priceImage?: string,
    public representationCategory?: ProductRepresentationCategory,
    public bookingMode?: ProductBookingMode,
  ) {}
}

export enum ProductStatus {
  ALL = '전체',
  TEST = '테스트',
  PENDING_REVIEW = '검수 대기',
  ENTERED = '오픈중',
  DISABLED = '비활성',
  END = '종료됨',
}

export enum OnlineMehtod {
  DIRECT = 'DIRECT',
  NONUNBUB = 'NONUNBUB',
}

export class ProductRequest {
  constructor(
    public id: number,
    public product: Product,
    public userId: number,
    public numberOfPeople: number,
    public phoneNumber: string,
    public message: string,
    public isChecked: boolean,
    public checkedAt?: Date,
  ) {}
}

export class ProductRequestCreateDto {
  constructor(
    public productId: number,
    public numberOfPeople: number,
    public message: string,
    public isChecked?: boolean,
  ) {}
}

export class Hashtag {
  constructor(
    public id: number,
    public name: string,
    public type?: HashtagType,
    public isAnalysis?: boolean,
    public createdAt?: Date,
    public updatedAt?: Date,
    public groupName?: string,
  ) {}
}

export class HashtagCreateDto {
  constructor(
    public id?: number,
    public name?: string,
    public type: HashtagType = HashtagType.PRODUCT,
    public isAnalysis = false,
  ) {}
}

export enum HashtagType {
  PRODUCT = 'PRODUCT',
  USER = 'USER',
}

export class ProductRepresentationPhoto {
  constructor(
    public id: number,
    public product: Product,
    public photo: string,
    public sortOrder: number,
  ) {}
}

export class ProductRepresentationPhotoCreateDto {
  constructor(
    public photo: string,
    public sortOrder: number,
    public file?: File,
  ) {}
}

export interface ProductSearchDto {
  page?: number;
  limit?: number;
  status?: ProductStatus;
  statusList?: ProductStatus[];
  hostId?: number;
  userId?: number;
  representationCategories?: string[];
  hashtags?: string[];
  analysisHashtags?: string[];
  recentlyOpenDays?: number;
  searchTerm?: string;
  isBusiness?: boolean;
  productIds?: number[];
  sidos?: string[];
  orderBy?: string;
  /** 사업단위 ID. `BUSINESS_GROUP_UNASSIGNED`(-1) 이면 미지정 상품만 조회. */
  businessGroupId?: number;
}

/** 상품의 사업단위 '미지정'을 뜻하는 센티넬 (서버의 동명 상수와 값이 같아야 한다). */
export const BUSINESS_GROUP_UNASSIGNED = -1;

export interface ProductUpdateDto {
  version?: string;
  title?: string;
  point?: string;
  recommend?: string;
  summaries?: ProductSummary[];
  description?: string;
  sections?: ProductSection[];
  courses?: CourseCreateDto[];
  address?: string;
  detailAddress?: string;
  howToCome?: string;
  runningDays?: number;
  runningHours?: number;
  runningMinutes?: number;
  reservationHours?: number;
  /** 예약 확정 방식. 생략하면 서버가 기존 값을 유지한다(`bookingMode`를 모르는 구버전 화면 저장에도 안전). */
  bookingMode?: ProductBookingMode;
  price?: number;
  /** 할인가는 쿠폰 파생값이라 서버가 무시한다(NNB-912) — 보내도 저장되지 않는다. */
  discountPrice?: number;
  /** 할인가 계산에 쓸 지정 쿠폰. null 이면 자동 선택(지정 해제)으로 바뀐다. */
  discountCouponId?: number | null;
  lat?: number;
  lon?: number;
  country?: string;
  sido?: string;
  sigugun?: string;
  dongmyun?: string;
  ri?: string;
  rest?: string;
  notice?: string;
  checkList?: string;
  includeList?: string;
  excludeList?: string;
  refundPolicy?: string;
  status?: ProductStatus;
  isBusiness?: boolean;
  hashtags?: HashtagCreateDto[];
  /** 테마 ID 목록. 빈 배열이면 전체 해제, **생략하면 서버가 매핑을 건드리지 않는다.** */
  themes?: number[];
  representationPhotos?: ProductRepresentationPhotoCreateDto[];
  color?: string;
  amenities?: Amenity[];
  isOffline?: boolean;
  onlineMethod?: OnlineMehtod;
  representationCategory?: ProductRepresentationCategory;
}

export interface ProductManageDto {
  status?: ProductStatus;
  badgeLabel?: string;
  hostCommissionRate?: number;
  // isPb는 서버가 호스트에서 파생시키므로 이 DTO로 보내지 않는다(보내면 400).
  isCommerce?: boolean;
  /** 테마 ID 목록. 빈 배열이면 전체 해제. 그룹별 상한 검증은 서버가 한다. */
  themes?: number[];
  hashtags?: string[];
  bizName?: string;
  agency?: string;
  agencyImage?: string;
  /** 소속 사업단위 ID. `-1` 이면 소속 해제. */
  businessGroupId?: number;
  /** 여행 진행 형태. isPb와 달리 파생값이 아니라 어드민이 직접 지정한다. */
  tripType?: TripType;
  memo?: string;
  price?: number;
  discountPrice?: number;
  specialDealLabelOnTop?: boolean;
  representationCategory?: ProductRepresentationCategory;
  hostId?: number;
  nonMemberPurchasable?: boolean;
  /** 할인가 계산에 쓸 지정 쿠폰. 비우면 자동 선택. NULL 은 "지정 없음"이라 유효값이다. */
  discountCouponId?: number | null;
}

export type ProductReviewResult = 'PENDING' | 'APPROVED' | 'REJECTED';

/** 검수 기록이 만들어진 경로. 검수 승인과 관리자 직접 오픈, 호스트 셀프 오픈을 구분한다. */
export type ProductReviewSource = 'REVIEW' | 'ADMIN_DIRECT' | 'HOST_SELF';

export class ProductConfirm {
  constructor(
    public id: number,
    public user: User,
    public product: Product,
    public rejectComment: string,
    /** @deprecated `reviewResult`로 대체됐다. 과거 데이터는 반려일 때 값이 비어 있다(NULL). */
    public isConfirm: boolean,
    public createdAt: Date,
    public updatedAt: Date,
    public reviewResult?: ProductReviewResult | null,
    public reviewerId?: number | null,
    public source?: ProductReviewSource | null,
    public isLatest?: boolean,
  ) {}
}

export interface ProductConfirmCreateDto {
  productId: number;
  rejectComment?: string;
  isConfirm?: boolean;
}

export interface ProductBadge {
  class: string;
  content: string;
}

export interface HashtagSearchDto {
  page?: number;
  limit?: number;
  isAnalysis?: boolean;
}

export class Course {
  constructor(
    public id: number,
    public product: Product,
    public title: string,
    public subTitle: string,
    public content: string,
    public note: string,
  ) {}
}

export class CourseCreateDto {
  constructor(
    public title: string,
    public subTitle: string,
    public content: string,
    public note: string,
  ) {}
}

export class Amenity {
  constructor(
    public id: number,
    public name: string,
  ) {}
}

export class Ticket {
  constructor(
    public id: number,
    public product: number | Product,
    public name: string,
    public date: string,
    public description: string,
    public isActivated: boolean,
    public isDeactivated: boolean,
    public isClosed: boolean,
    public isReservationEnd: boolean,
    public isOld: boolean,
    public isHidden: boolean,
    public badges?: TicketBadge[] | number[],
    public endStandard?: EndStandard,
    public prices?: TicketPrice[],
    public parent?: Ticket,
    public options?: Ticket[],
    public requestBadgeDescription?: string,
    public group?: TicketGroup,
    public minParticipants?: number,
    public maxParticipants?: number,
    public pricePerPerson?: number,
    public maxTeamCapacity?: number,
    public minParticipantsPerTeam?: number,
    public maxParticipantsPerTeam?: number,
    public pricePerTeam?: number,
    public extraPricePerPerson?: number,
    public descriptions?: string[],
    public minPrice?: ExpectedPrice,
    public participantCount?: number,
    public orderItemCount?: number,
    public isFullyBooked?: boolean,
  ) {}
}

export class TicketBadge {
  constructor(
    public id: number,
    public ticket: number | Ticket,
    public name: string,
  ) {}
}

export class TicketPrice {
  constructor(
    public id: number,
    public ticket: number | Ticket,
    public quantity: number,
    public price: number,
    public discountPrice: number,
  ) {}
}

export enum EndStandard {
  PER_PERSON = '인원별',
  PER_TEAM = '팀별',
}

export class TicketCreateBulkDto {
  boardingSourceTicketId?: number;
  constructor(
    public productId: number,
    public group: TicketGroup,
    public tickets: TicketCreateDto[],
    /** 패키지 상품 전용. 지정하면 서버가 회차 날짜마다 출발 코스를 찾거나 만들어 티켓에 연결한다. */
    public departureCityCodes?: string[],
  ) {}
}

export class TicketGroup {
  boardingLocationIds?: string[];
  constructor(
    public name: string,
    public color: string,
    public from: Date,
    public to: Date,
    public cron: string,
    public dowCron: string,
    public weekString: string,
    public hours: number,
    public minutes: number,
    public optionTicketCount: number,
    public closedDates?: string | Date[],
    public id?: number,
    // 서버가 판매중 회차 **개수**를 담는다(`loadRelationCountAndMap`). 템플릿들이 이미 `> 0`·`== 0`으로
    // 비교하고 있어 선언만 boolean 으로 어긋나 있었다.
    public activatedTicketCount?: number,
    public tickets?: Ticket[],
    public boardingLocations?: string | string[],
    public collectParticipantInfo?: boolean,
    public insuranceOption?: import('./insurance-option.enum').InsuranceOption,
    public infantChildAllowed?: boolean,
    public phoneRequired?: boolean,
    public customFields?: import('./custom-field.interface').CustomField[],
    public hasPaymentHistory?: boolean,
  ) {}
}

export class TicketCreateDto {
  public id?: number;

  constructor(
    public name: string,
    public date: Date,
    public badges: TicketBadgeCreateDto[],
    public prices: TicketPriceCreateDto[],
    public requestBadgeDescription: string,
    public endStandard: EndStandard,
    public minParticipants: number,
    public maxParticipants: number,
    public pricePerPerson: number,
    public maxTeamCapacity: number,
    public minParticipantsPerTeam: number,
    public maxParticipantsPerTeam: number,
    public pricePerTeam: number,
    public extraPricePerPerson: number,
    public description?: string,
    public isActivated?: boolean,
    public isDeactivated?: boolean,
    public isClosed?: boolean,
    public isReservationEnd?: boolean,
    public isOld?: boolean,
    public isHidden?: boolean,
  ) {}
}

export class TicketBadgeCreateDto {
  constructor(public name: string) {}
}

export class TicketPriceCreateDto {
  constructor(
    public quantity: number,
    public price: number,
  ) {}
}

export interface TicketSearchDto {
  productId?: number[];
  from?: string;
  to?: string;
  isOld?: boolean;
}

export interface ExpectedPrice {
  quantity: number;
  price: number;
  discountPrice: number;
  discountPercent: number;
  pricePerPerson?: number;
}

export class Section {
  listRevision?: string;

  constructor(
    public id: number,
    public subTitle: string,
    public title1: string,
    public title2: string,
    public title1Strong: boolean,
    public title2Strong: boolean,
    public order: number,
    public type: SectionType,
    public productSectionMap: ProductSectionMap[],
    public products: Product[],
    public moreLink?: string,
    public moreLabel?: string,
    public icon?: string,
    public adSlotCode?: string | null,
  ) {}
}

export interface ProductSectionMap {
  id: number;
  productId: number;
  sectionId: number;
  order: number;
  product?: Product;
}

export enum SectionType {
  HORIZONTAL = '미디움 사이즈 상품 가로스크롤',
  VERTICAL = '라지 사이즈 상품 세로스크롤',
  LIST = '스몰 사이즈 상품 세로스크롤',
  LIST2 = '컴팩트 리스트 상품',
  FEED = '피드',
  INFLUENCER = '인플루언서',
  LIGHT = '벙개모임',
  BESTCATEGORY = '카테고리인기',
  ACCOMPANY = '동행자별',
  IMMINENT_DEPARTURE = '바로출발',
  RANKING = '랭킹',
  BENEFIT_BANNER = '혜택배너',
  KEYWORD_RECOMMEND = '검색어추천',
  GROUP_TRIP = '단체여행',
  REGION_SHORTCUT = '지역바로가기',
  RECENTLY_VIEWED = '최근본상품',
  AD_SLOT = '광고슬롯',
}

export const SectionLayout = {
  HORIZONTAL: 'HORIZONTAL',
  VERTICAL: 'VERTICAL',
  LIST: 'LIST',
  FEED: 'FEED',
  INFLUENCER: 'INFLUENCER',
  LIGHT: 'LIGHT',
  BESTCATEGORY: 'BESTCATEGORY',
  ACCOMPANY: 'ACCOMPANY',
} as const;

export type SectionLayout = (typeof SectionLayout)[keyof typeof SectionLayout];

export enum EventSectionStyle {
  LIST1 = 'LIST1',
  LIST2 = 'LIST2',
  VERTICAL = 'VERTICAL',
  HORIZONTAL = 'HORIZONTAL',
}

export class SectionUpdateDto {
  constructor(
    public sections: SectionDto[],
    public baseIds?: number[],
    public baseRevisions?: Record<string, string>,
  ) {}
}

export class SectionDto {
  constructor(
    public id: number,
    public subTitle: string,
    public title1: string,
    public title2: string,
    public title1Strong: boolean,
    public title2Strong: boolean,
    public order: number,
    public type: SectionType,
    public productSectionMap: ProductSectionDto[],
    public moreLabel?: string,
    public moreLink?: string,
    public icon?: string,
    public adSlotCode?: string | null,
  ) {}
}

export class ProductSectionDto {
  constructor(
    public productId: number,
    public sectionId: number,
    public id?: number,
    public order?: number,
  ) {}
}

export class ProductSection {
  title: string;
  paragraphs: Paragraph[];
}

export class Paragraph {
  title: string;
  time: string;
  description: string;
  note: string;
  photos: string[];
}

export class ProductSummary {
  photos: string[];
  title: string;
  description: string;
}

export enum ProductRepresentationCategory {
  ACCOMMODATION = '숙박',
  ONEDAY = '당일',
  LEARN = '배움',
  LIGHT = '벙개',
  EXPERIENCE = '체험',
  DINING = '다이닝',
  SPECIALTY = '특산품',
}

/**
 * 여행 진행 형태 — 고객에게 보여줄 값(알림톡 문구·상품 안내)이다.
 *
 * 판정 기준은 "차량이 포함되는가"가 아니라 **"출발지에서 태워 가는가"**다. 현지 픽업이나
 * 현지 이동차량만 있는 상품은 고객이 그 지역까지 스스로 오므로 `SELF_VISIT`이다.
 *
 * 가이드 배정·설문 템플릿 같은 내부 운영 분기는 `isPb`가 담당하므로 이 값으로 대체하지 말 것.
 */
export enum TripType {
  PACKAGE = 'PACKAGE',
  SELF_VISIT = 'SELF_VISIT',
}

export class ProductArchive {
  constructor(
    public id: number,
    public productId: number,
    public name: string,
    public createdAt: Date,
    public data?: Record<string, any>,
    // 보관 당시의 상세페이지 버전(1=리뉴얼 전, 2=요약/섹션 구성).
    // v1을 v2 상품에 복원하면 요약·섹션이 삭제된다. 반대 방향은 구성이 바뀐다.
    // 목록 API가 값을 못 주는 옛 응답에서는 null 이 온다 — 그때는 경고하지 않는다.
    // `ProductService.toArchive` 가 undefined 를 null 로 모아 주므로 두 상태만 존재한다.
    public version: number | null = null,
  ) {}
}
